import { usePageTitle } from '@/hooks/use-document-title'
import Footer from './footer'

export default function CommunityGuidelines() {
  usePageTitle('Community Guidelines')

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col">
      <div className="container mx-auto max-w-3xl px-4 py-12">
        <h1 className="mb-2 text-3xl font-bold tracking-tight">
          Community Guidelines
        </h1>
        <p className="text-muted-foreground mb-8 text-sm">
          Last updated: March 2026
        </p>
        <p className="text-muted-foreground mb-10 text-base leading-relaxed">
          Beyond CTCI requires professional, respectful, and honest conduct from
          all members. These guidelines establish expectations for behavior
          across all interactions on the platform, including but not limited to
          coursework, peer interviews, coaching sessions, and community feedback
          channels. By using the platform, you agree to abide by these
          guidelines. Violations may result in warnings, suspension, or
          permanent removal at Beyond CTCI's sole discretion.
        </p>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">A Learning Community</h2>
          <p className="text-muted-foreground mb-3 leading-relaxed">
            Beyond CTCI exists to help people prepare for technical interviews.
            That means everyone here — regardless of experience level — is
            working to improve. Members will be at different stages of their
            journey, and that is the point.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Be patient with those who know less than you. Be humble with those
            who know more. Offer encouragement where you can, and remember that
            you are interacting with someone who has chosen to put in the work
            to get better. Respect that effort.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">Your Voice Matters</h2>
          <p className="text-muted-foreground mb-3 leading-relaxed">
            Beyond CTCI is built with its members in mind, and your perspective
            directly shapes the platform. We actively encourage you to share bug
            reports, feature requests, and feedback on our public roadmap at{' '}
            <a
              href="https://roadmap.beyondctci.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-4 hover:opacity-80"
            >
              roadmap.beyondctci.dev
            </a>
            . This is where you can see what's being worked on, vote on what
            matters most to you, and propose ideas of your own.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Your insight as someone actively using this platform to prepare for
            interviews is invaluable — you see things the development team
            cannot. Whether it's a workflow that feels clunky, a feature that
            would save you time, or a bug that's getting in your way, we want to
            hear it. The same standards of respect and relevance apply in these
            channels: keep feedback constructive, on-topic, and focused on the
            platform — not directed at individuals.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">Respect and Harassment</h2>
          <p className="text-muted-foreground mb-3 leading-relaxed">
            This is a community of people helping each other through a stressful
            process. That requires a baseline of mutual respect. Disagree with
            an approach, challenge an idea, give honest feedback — but do it in
            a way that treats the other person as a professional peer.
            Harassment of any kind is prohibited, including but not limited to:
          </p>
          <ul className="text-muted-foreground mb-3 list-disc space-y-2 pl-6">
            <li>
              Targeted hostility, personal attacks, intimidation, or repeated
              unwelcome contact
            </li>
            <li>Sexual or objectifying comments, imagery, or behavior</li>
            <li>
              Language or content that demeans, threatens, or dehumanizes
              individuals or groups based on race, ethnicity, religion,
              disability, age, veteran status, sexual orientation, gender, or
              gender identity
            </li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">
            Privacy and Confidentiality
          </h2>
          <p className="text-muted-foreground mb-3 leading-relaxed">
            Do not share personally identifiable information (PII) of others
            without their explicit consent. This includes personal details
            shared during peer interviews, coaching sessions, or any other
            platform interaction. What is discussed in an interview stays in
            that interview unless both parties agree otherwise.
          </p>
          <p className="text-muted-foreground mb-3 leading-relaxed">
            This extends to any context where private information might surface
            — a peer mentions their employer during a mock interview, a coaching
            client shares their job search timeline, or a member discloses
            personal circumstances in conversation. None of that is yours to
            repeat or share elsewhere.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            If you come across private or sensitive information being shared
            without consent — whether in feedback channels, interviews, or
            anywhere else on the platform — report it to{' '}
            <a
              href="mailto:support@beyondctci.dev"
              className="text-primary underline underline-offset-4 hover:opacity-80"
            >
              support@beyondctci.dev
            </a>{' '}
            so it can be addressed promptly.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">Intellectual Honesty</h2>
          <p className="text-muted-foreground mb-3 leading-relaxed">
            Interview preparation depends on trust. Members are expected to act
            with integrity in all aspects of their participation on the
            platform:
          </p>
          <ul className="text-muted-foreground mb-3 list-disc space-y-2 pl-6">
            <li>
              <strong className="text-foreground">
                Do not share proprietary interview questions
              </strong>{' '}
              obtained under NDA or confidentiality agreements from employers or
              recruiting processes. If a company shared it with you in
              confidence, it does not belong here.
            </li>
            <li>
              <strong className="text-foreground">
                Give credit where it is due.
              </strong>{' '}
              If you reference or adapt someone else's solution, approach, or
              explanation, acknowledge the source.
            </li>
            <li>
              <strong className="text-foreground">
                Represent your own work honestly.
              </strong>{' '}
              Do not present others' solutions as your own during peer
              interviews or in any platform interaction.
            </li>
            <li>
              <strong className="text-foreground">
                Do not misuse AI-generated content
              </strong>{' '}
              in ways that misrepresent your understanding during peer
              interviews or coaching sessions.
            </li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">Inappropriate Content</h2>
          <p className="text-muted-foreground leading-relaxed">
            Offensive, explicit, unlawful, or excessively graphic content is
            prohibited. Keep all discussions and submissions professional.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">Spam and Solicitation</h2>
          <p className="text-muted-foreground leading-relaxed">
            Do not submit repetitive, irrelevant, or promotional content.
            Unsolicited promotions, phishing attempts, and advertising of
            products or services are strictly prohibited.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">Impersonation</h2>
          <p className="text-muted-foreground leading-relaxed">
            Misrepresenting your identity or impersonating another person or
            organization is prohibited. Be honest about who you are in all
            interactions — through usernames, profiles, and direct
            communication.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 text-xl font-semibold">Enforcement</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you see content or behavior that violates these guidelines,
            report it to{' '}
            <a
              href="mailto:support@beyondctci.dev"
              className="text-primary underline underline-offset-4 hover:opacity-80"
            >
              support@beyondctci.dev
            </a>
            . All reports will be reviewed. Enforcement actions — including
            warnings, suspension, or permanent removal — are taken at Beyond
            CTCI's sole discretion to maintain the safety and integrity of the
            community.
          </p>
        </section>

        <div className="border-t pt-6">
          <p className="text-muted-foreground italic">
            Thank you for being part of Beyond CTCI and for helping maintain a
            community where everyone can focus on what they came here to do: get
            better.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  )
}
